/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import gql from 'graphql-tag';
import { isObject, reduce, split, startsWith } from 'lodash';

import {
  getAttributeFragment,
  getCustomAttributeFragment,
  getMappedAttributeFragment,
} from '../listing/AttributesQueryConfig';

// TODO: refactor: most of this is the same as in `SorListing.ts` and should be reused from there,
//                 also consider just passing in a custom gql defintion to use for a given page/view
//                 to a generic listing query constructor
export default (
  category: string,
  objectType: string,
  isCommentable: boolean,
  columns: Array<string | object>,
  order: string,
  pageSize: number,
  currPage: number
) => {
  // TODO: temp, send the objecttype in the same format as in sor listing (ModelMetaSchema.modelSingular)
  let objectTypeSingular = objectType;
  if (objectType == 'assessments') objectTypeSingular = 'Assessment';

  const regularColumns: string[] = columns.filter(
    (column: string | object) => !isObject(column) && !startsWith(column, 'mappings.')
  ) as string[];
  const customAttributeColumns: object[] = columns.filter(
    (column: string | object) => isObject(column) && startsWith(column.name, 'ca__')
  ) as object[];
  const mappedColumns: string[] = columns.filter(
    (column: string | object) => !isObject(column) && startsWith(column, 'mappings.')
  ) as string[];

  /**
   * Loops over queried mapped objects columns and constructs custom attributes query
   * Example:
   * `
   *   ca__1: customAttribute(name: ca__1) {
   *     name
   *     ... on CustomAttributeDate {
   *       value
   *     }
   *   }
   * `
   */
  // TODO: rename other two queries
  const customAttributesColumnsQuery = reduce(
    customAttributeColumns,
    (s: string, caColumn: object) => {
      s += `${getCustomAttributeFragment(caColumn.name, caColumn.type)}\n`;
      return s;
    },
    ''
  );

  /**
   * Loops over queried columns and constructs query with `{ value }` sub-selection
   * Example:
   * `
   *   id // Does not need sub-selection
   *   title {
   *     value
   *   }
   * `
   */
  const regularColumnsWithSubSelections = reduce(
    regularColumns,
    (s: string, column) => {
      if (getAttributeFragment(objectTypeSingular, column))
        s += `${getAttributeFragment(objectTypeSingular, column)}\n`;
      return s;
    },
    ''
  );

  /**
   * Loops over queried mapped objects columns and constructs mappings query
   * Example:
   * `
   *   mappings {
   *     programs {
   *       edges {
   *         node {
   *           id
   *           sorCode { value }
   *           sorTitle { value }
   *         }
   *       }
   *     }
   *   }
   * `
   */
  const mappedColumnsWithSubSelections = reduce(
    mappedColumns,
    (s: string, column: string) => {
      /**
       * Takes the object type from mappings column key,
       * i.e.: 'program' from 'mappings.program'
       */
      const objectType: string = split(column, '.')[1];

      s += `${getMappedAttributeFragment(objectType)}\n`;
      return s;
    },
    ''
  );
  const mappedColumnsFragment = mappedColumnsWithSubSelections
    ? `
      mappings {
        ${mappedColumnsWithSubSelections}
      }
    `
    : ``;

  return gql`
    query TodoListObjects($currentUserFilter: JSONString, $objectsFilter: JSONString) {
      search(
        type: Person,
        filter: $currentUserFilter,
      ) {
        totalCount
        edges {
          node {
            id
            ... on Person {
              todos {
                ${category} {
                  ${objectType}(first: ${pageSize}, page: ${currPage}, filter: $objectsFilter, order: ${order}) {
                    totalCount
                    edges {
                      node {
                        id
                        __typename
                        dbId
                        ${isCommentable ? 'hasUnreadComments' : ''}

                        ${regularColumnsWithSubSelections}
                        ${customAttributesColumnsQuery}
                        ${mappedColumnsFragment}
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
};
